import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'

/**
 * The catalogue's wire types and hooks.
 *
 * Every cost-derived field is optional on purpose. The server omits them for a
 * user without `product.cost.view` rather than sending null, so "absent" is the
 * normal case for a cashier and the screens must treat it as such — never as zero.
 */

export type UnitOfMeasure = 'Piece' | 'Roll' | 'Box' | 'Metre'
export type VatClass = 'Standard' | 'ZeroRated' | 'Exempt'
export type Fragility = 'NotFragile' | 'Low' | 'Medium' | 'High' | 'ExtremelyFragile'
export type MarginOutcome = 'Acceptable' | 'BelowMinimum' | 'AboveMaximum'

export type Paged<T> = { items: T[]; total: number; page: number; pageSize: number }

export type ProductGroup = {
  id: string
  code: string
  name: string
  sortOrder: number
  isActive: boolean
  salesAccountCode: string
  productCount: number
  /** The tree (D-159). The list arrives in path order, so rendering needs no sorting. */
  parentId?: string
  depth: number
  slug: string
  path: string
  showOnWeb: boolean
  webTitle?: string
  webDescription?: string
  metaDescription?: string
  /** This category and everything beneath it — what says whether it is safe to move or retire. */
  productCountIncludingChildren: number
}

export type Brand = {
  id: string
  name: string
  slug: string
  description?: string
  sortOrder: number
  isActive: boolean
  showOnWeb: boolean
  productCount: number
}

export type ProductListItem = {
  id: string
  code: string
  name: string
  groupId: string
  groupName: string
  barcode?: string
  unitOfMeasure: UnitOfMeasure
  vatClass: VatClass
  retailPrice: number
  wholesalePrice: number
  resellerPrice: number
  quantityOnHand: number
  quantityAvailable: number
  belowReorderLevel: boolean
  fragility: Fragility
  hasWarranty: boolean
  isActive: boolean
  imageUrl?: string
  costPrice?: number
  retailMarginPercent?: number
  stockValue?: number
  /** What a pack costs. Zero where the product is not sold by the pack (D-204). */
  packPrice: number
  packSize: number
  /** "Box of 10" — set only where it can actually be sold by the pack. */
  packDescription?: string
  /** "carton", "box" — what one is called, for a quantity entered in them (D-257). */
  packLabel?: string
}

export type Warranty = {
  isAvailable: boolean
  periodMonths: number
  terms?: string
  repairCentre?: string
  replacementPolicy?: string
}

export type MarginPolicy = {
  minimumMarginPercent?: number | null
  maximumMarginPercent?: number | null
  maximumDiscountPercent?: number | null
  maximumDiscountValue?: number | null
}

export type MarginCheck = { tier: string; price: number; marginPercent?: number; outcome: MarginOutcome }

export type ProductImage = { id: string; url: string; contentType: string; sizeBytes: number; caption?: string; isPrimary: boolean }

export type ProductUpsell = { id: string; code: string; name: string; retailPrice: number; imageUrl?: string; isActive: boolean }

export type ProductDetail = {
  id: string
  code: string
  name: string
  groupId: string
  groupName: string
  barcode?: string
  barcodeIsInStore: boolean
  unitOfMeasure: UnitOfMeasure
  vatClass: VatClass
  description?: string
  isActive: boolean
  prices: {
    retailPrice: number; wholesalePrice: number; resellerPrice: number; costPrice?: number; minimumSellingPrice?: number
    /** What a whole pack costs. Zero where it is not sold by the pack (D-204). */
    packPrice: number
    /** What one is called — "Box", "Carton". Null reads as "Pack". */
    packLabel?: string
    /** How many pieces are in one, from the product's online selling rules. */
    packSize: number
  }
  specifications?: string
  instructions?: string
  manufacturer?: string
  countryOfOrigin?: string
  talkingPoints?: string
  features?: string
  benefits?: string
  rating?: number
  fragility: Fragility
  warranty: Warranty
  images: ProductImage[]
  upsells: ProductUpsell[]
  createdAt: string
  modifiedAt?: string
  margins?: MarginPolicy
  marginChecks?: MarginCheck[]
  lowestPriceForMinimumMargin?: number
  brandId?: string
  brandName?: string
  web: ProductWeb
  selling: ProductSelling
}

export type ProductVideo = {
  url: string
  /** TikTok, Facebook, Instagram or YouTube — worked out from the link by the server. */
  host: string
  label: string
}

export type ProductWeb = {
  isPublished: boolean
  slug?: string
  title?: string
  description?: string
  metaDescription?: string
  launchedOn?: string
  sortScore: number
  /** What the website charges for one, where it differs. Absent means the shop's retail price. */
  webPrice?: number
  /** What the website charges for a box. Absent means the shop's box price. */
  webPackPrice?: number
  offerPercent?: number
  offerFrom?: string
  offerTo?: string
  /** Whether the offer is running today — the dates worked out by the server (D-215). */
  offerRunning: boolean
  /** What a shopper pays online today, offer and all. */
  listedPrice: number
  /** What they pay for a box online today. Nought where no box is sold. */
  listedPackPrice: number
  videos: ProductVideo[]
}

export type ProductSelling = {
  packSize: number
  minimumQuantity: number
  wholesaleMinimumQuantity: number
  maximumQuantity?: number
}

export type StockAtLocation = {
  locationId: string
  locationCode: string
  locationName: string
  quantityOnHand: number
  quantityReserved: number
  quantityQuarantined: number
  quantityWebAllocated: number
  quantityAvailable: number
  reorderLevel: number
  reorderQuantity: number
  binLocation?: string
  belowReorderLevel: boolean
  averageCost?: number
  stockValue?: number
}

/**
 * What one shop charges for a product (D-276).
 *
 * The shop's own figures are undefined wherever it follows the catalogue, and the `effective*`
 * ones are what the till will actually charge. Both come from the server: working the second out
 * here would be a second implementation of the rule, and the one in the browser would be the one
 * nobody notices going wrong.
 */
export type ShopPrice = {
  locationId: string
  locationCode: string
  locationName: string
  locationType: 'Warehouse' | 'Shop' | 'ResellerVirtual'
  retailPrice?: number
  wholesalePrice?: number
  resellerPrice?: number
  minimumSellingPrice?: number
  packPrice?: number
  reason?: string
  effectiveRetailPrice: number
  effectiveWholesalePrice: number
  effectiveResellerPrice: number
  effectiveMinimumSellingPrice: number
  effectivePackPrice: number
  /** Whether this shop says anything of its own at all. */
  isSet: boolean
}

export type ShopPriceInput = {
  retailPrice?: number | null
  wholesalePrice?: number | null
  resellerPrice?: number | null
  minimumSellingPrice?: number | null
  packPrice?: number | null
  reason?: string | null
  confirmAboveMaximum: boolean
}

export type StockMovement = {
  id: string
  occurredAt: string
  locationCode: string
  movementType: string
  quantityIn: number
  quantityOut: number
  runningBalance: number
  sourceDocumentType: string
  sourceDocumentCode?: string
  notes?: string
  unitCost?: number
}

export type ImportReport = {
  rows: number
  toCreate: number
  toUpdate: number
  errors: { rowNumber: number; message: string }[]
  applied: boolean
  isValid: boolean
}

export type ProductFilters = {
  search: string
  groupId: string
  status: 'active' | 'inactive' | 'all'
  lowStock: boolean
  page: number
  /** Stock at one shop only (the till's shelf); every visible shop otherwise. */
  locationId?: string
}

export const productKeys = {
  all: ['products'] as const,
  list: (filters: ProductFilters) => ['products', 'list', filters] as const,
  detail: (id: string) => ['products', 'detail', id] as const,
  stock: (id: string) => ['products', 'stock', id] as const,
  shopPrices: (id: string) => ['products', 'shop-prices', id] as const,
  movements: (id: string, page: number, locationId: string) => ['products', 'movements', id, page, locationId] as const,
  groups: ['product-groups'] as const,
}

export const PAGE_SIZE = 50

/**
 * @param refreshMs
 * How often to ask again while the page is open. The catalogue screens leave it off — a product
 * list does not change under somebody editing it. The till passes one, because the stock figure on
 * a tile is the thing another counter has just changed: a basket at Counter 2 holds stock the
 * moment it is scanned (D-193), and Counter 1 would otherwise keep being told the old number until
 * it happened to search for something.
 */
export function useProducts(filters: ProductFilters, refreshMs?: number) {
  return useQuery({
    queryKey: productKeys.list(filters),
    refetchInterval: refreshMs,
    refetchIntervalInBackground: false,
    queryFn: ({ signal }) => {
      const query = new URLSearchParams({
        status: filters.status,
        page: String(filters.page),
        pageSize: String(PAGE_SIZE),
      })
      if (filters.search.trim()) query.set('search', filters.search.trim())
      if (filters.groupId) query.set('groupId', filters.groupId)
      if (filters.lowStock) query.set('lowStock', 'true')
      if (filters.locationId) query.set('locationId', filters.locationId)
      return api<Paged<ProductListItem>>(`/api/products?${query}`, { signal })
    },
    // Keep the previous page on screen while the next loads, so typing in the
    // search box does not blank the table on every keystroke.
    placeholderData: keepPreviousData,
  })
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: ({ signal }) => api<ProductDetail>(`/api/products/${id}`, { signal }),
  })
}

export function useProductStock(id: string) {
  return useQuery({
    queryKey: productKeys.stock(id),
    queryFn: ({ signal }) => api<StockAtLocation[]>(`/api/products/${id}/stock`, { signal }),
  })
}

export function useShopPrices(id: string, enabled = true) {
  return useQuery({
    queryKey: productKeys.shopPrices(id),
    enabled,
    queryFn: ({ signal }) => api<ShopPrice[]>(`/api/products/${id}/shop-prices`, { signal }),
  })
}

/**
 * Sets or clears one shop's prices.
 *
 * Clearing sends DELETE rather than five blanks, because a row of nulls and no row are the same
 * thing to the till but not to anybody reading the screen afterwards.
 */
export function useSetShopPrice(id: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ locationId, input }: { locationId: string; input: ShopPriceInput | null }) =>
      api<ShopPrice[]>(`/api/products/${id}/shop-prices/${locationId}`, {
        method: input === null ? 'DELETE' : 'PUT',
        ...(input === null ? {} : { body: input }),
      }),
    onSuccess: (prices) => {
      client.setQueryData(productKeys.shopPrices(id), prices)
      void client.invalidateQueries({ queryKey: ['products', 'list'] })
    },
  })
}

export function useProductMovements(id: string, page: number, locationId: string) {
  return useQuery({
    queryKey: productKeys.movements(id, page, locationId),
    queryFn: ({ signal }) => {
      const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) })
      if (locationId) query.set('locationId', locationId)
      return api<Paged<StockMovement>>(`/api/products/${id}/movements?${query}`, { signal })
    },
    placeholderData: keepPreviousData,
  })
}

export function useProductGroups() {
  return useQuery({
    queryKey: productKeys.groups,
    queryFn: ({ signal }) => api<ProductGroup[]>('/api/product-groups', { signal }),
    staleTime: 5 * 60_000,
  })
}

/**
 * A mutation that replaces the product in the cache with the server's answer.
 *
 * Every product write returns the full detail, so the screen shows what the server
 * actually stored — rounding, generated barcode, promoted primary picture —
 * rather than what the form assumed it would.
 */
function useProductWrite<TInput>(id: string, send: (input: TInput) => Promise<ProductDetail>) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: send,
    onSuccess: (detail) => {
      client.setQueryData(productKeys.detail(id), detail)
      void client.invalidateQueries({ queryKey: ['products', 'list'] })
    },
  })
}

export type UpdateProductInput = {
  name: string
  groupId: string
  unitOfMeasure: UnitOfMeasure
  vatClass: VatClass
  barcode?: string
  description?: string
  specifications?: string
  instructions?: string
  manufacturer?: string
  countryOfOrigin?: string
  talkingPoints?: string
  features?: string
  benefits?: string
  rating?: number | null
  fragility: Fragility
  warranty: Warranty
}

export function useUpdateProduct(id: string) {
  return useProductWrite(id, (input: UpdateProductInput) =>
    api<ProductDetail>(`/api/products/${id}`, { method: 'PUT', body: input }),
  )
}

export type PricingInput = {
  costPrice: number
  retailPrice: number
  wholesalePrice: number
  resellerPrice: number
  minimumSellingPrice?: number | null
  margins: MarginPolicy
  confirmAboveMaximum: boolean
  /** What a whole pack costs. Zero means it is not sold by the pack at all (D-204). */
  packPrice: number
  /** How many are in one. Set here with the price, because neither is any use alone (D-214). */
  packSize: number
  packLabel?: string | null
}

export function useUpdatePricing(id: string) {
  return useProductWrite(id, (input: PricingInput) =>
    api<ProductDetail>(`/api/products/${id}/prices`, { method: 'PUT', body: input }),
  )
}

export function useUpdateUpsells(id: string) {
  return useProductWrite(id, (productIds: string[]) =>
    api<ProductDetail>(`/api/products/${id}/upsells`, { method: 'PUT', body: { productIds } }),
  )
}

export function useSetActive(id: string) {
  return useProductWrite(id, (active: boolean) =>
    api<ProductDetail>(`/api/products/${id}/${active ? 'activate' : 'deactivate'}`, { method: 'POST', body: {} }),
  )
}

export function useUploadImage(id: string) {
  return useProductWrite(id, ({ file, caption }: { file: File; caption?: string }) => {
    const form = new FormData()
    form.append('file', file)
    if (caption) form.append('caption', caption)
    return api<ProductDetail>(`/api/products/${id}/images`, { method: 'POST', body: form })
  })
}

export function useDeleteImage(id: string) {
  return useProductWrite(id, (imageId: string) =>
    api<ProductDetail>(`/api/products/${id}/images/${imageId}`, { method: 'DELETE' }),
  )
}

export function usePrimaryImage(id: string) {
  return useProductWrite(id, (imageId: string) =>
    api<ProductDetail>(`/api/products/${id}/images/${imageId}/primary`, { method: 'PUT', body: {} }),
  )
}

export function useUpdateReorder(id: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { locationId: string; reorderLevel: number; reorderQuantity: number; binLocation?: string }) =>
      api<StockAtLocation[]>(`/api/products/${id}/stock/${input.locationId}/reorder`, {
        method: 'PUT',
        body: { reorderLevel: input.reorderLevel, reorderQuantity: input.reorderQuantity, binLocation: input.binLocation },
      }),
    onSuccess: (stock) => {
      client.setQueryData(productKeys.stock(id), stock)
      void client.invalidateQueries({ queryKey: ['products', 'list'] })
    },
  })
}

export type CreateProductInput = {
  code?: string
  name: string
  groupId: string
  unitOfMeasure: UnitOfMeasure
  vatClass: VatClass
  barcode?: string
  costPrice: number
  retailPrice: number
  wholesalePrice: number
  resellerPrice: number
  minimumSellingPrice?: number | null
  margins?: MarginPolicy
  confirmAboveMaximum: boolean
  description?: string
}

export function useCreateProduct() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateProductInput) =>
      api<ProductDetail>('/api/products', { method: 'POST', body: input }),
    onSuccess: (detail) => {
      client.setQueryData(productKeys.detail(detail.id), detail)
      void client.invalidateQueries({ queryKey: productKeys.all })
      void client.invalidateQueries({ queryKey: productKeys.groups })
    },
  })
}

export function useImportProducts() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ file, dryRun }: { file: File; dryRun: boolean }) => {
      const form = new FormData()
      form.append('file', file)
      return api<ImportReport>(`/api/products/import?dryRun=${dryRun}`, { method: 'POST', body: form })
    },
    onSuccess: (report) => {
      if (report.applied) {
        void client.invalidateQueries({ queryKey: productKeys.all })
        void client.invalidateQueries({ queryKey: productKeys.groups })
      }
    },
  })
}

export function useSaveGroup() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string; code?: string; name: string; sortOrder: number; salesAccountCode?: string; isActive?: boolean; parentId?: string | null }) =>
      api<ProductGroup>(id ? `/api/product-groups/${id}` : '/api/product-groups', {
        method: id ? 'PUT' : 'POST',
        body,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: productKeys.groups }),
  })
}

/** Whether a product is on the storefront, what it says there, and which make it is. */
export function useProductWeb(id: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (body: {
      isPublished: boolean
      brandId?: string | null
      title?: string | null
      description?: string | null
      metaDescription?: string | null
      sortScore: number
      slug?: string | null
      webPrice?: number | null
      webPackPrice?: number | null
      offerPercent?: number | null
      offerFrom?: string | null
      offerTo?: string | null
      videoLinks?: string[]
    }) => api<ProductDetail>(`/api/products/${id}/web`, { method: 'PUT', body }),
    onSuccess: (product) => {
      client.setQueryData(productKeys.detail(id), product)
      void client.invalidateQueries({ queryKey: productKeys.all })
    },
  })
}

/** Pack size, and how many earn the wholesale price online. */
export function useProductSelling(id: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (body: {
      packSize: number
      minimumQuantity: number
      wholesaleMinimumQuantity: number
      maximumQuantity?: number | null
    }) => api<ProductDetail>(`/api/products/${id}/selling`, { method: 'PUT', body }),
    onSuccess: (product) => {
      client.setQueryData(productKeys.detail(id), product)
      void client.invalidateQueries({ queryKey: productKeys.all })
    },
  })
}

/** How much of this product the storefront may sell from one location. */
export function useWebAllocation(id: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ locationId, quantity }: { locationId: string; quantity: number }) =>
      api<StockAtLocation[]>(`/api/products/${id}/stock/${locationId}/web-allocation`, {
        method: 'PUT',
        body: { quantity },
      }),
    onSuccess: (stock) => client.setQueryData(productKeys.stock(id), stock),
  })
}

/** Moves a category in the tree. The server rewrites every path beneath it. */
export function useMoveGroup() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      api<ProductGroup[]>(`/api/product-groups/${id}/parent`, { method: 'PUT', body: { parentId } }),
    onSuccess: (groups) => {
      client.setQueryData(productKeys.groups, groups)
      void client.invalidateQueries({ queryKey: productKeys.groups })
    },
  })
}

/** What the storefront shows for a category, and at what address. */
export function useGroupWeb() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string
      showOnWeb: boolean
      slug?: string
      webTitle?: string | null
      webDescription?: string | null
      metaDescription?: string | null
    }) => api<ProductGroup[]>(`/api/product-groups/${id}/web`, { method: 'PUT', body }),
    onSuccess: (groups) => {
      client.setQueryData(productKeys.groups, groups)
      void client.invalidateQueries({ queryKey: productKeys.groups })
    },
  })
}

export function useBrands() {
  return useQuery({
    queryKey: ['brands'],
    queryFn: ({ signal }) => api<Brand[]>('/api/brands', { signal }),
    staleTime: 5 * 60_000,
  })
}

export function useSaveBrand() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...body }: {
      id?: string
      name: string
      slug?: string | null
      description?: string | null
      sortOrder: number
      isActive?: boolean
      showOnWeb?: boolean
    }) => api<Brand>(id ? `/api/brands/${id}` : '/api/brands', { method: id ? 'PUT' : 'POST', body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['brands'] })
      void client.invalidateQueries({ queryKey: productKeys.all })
    },
  })
}

/** Display labels. The enum names are for the wire, not for people. */
export const unitLabels: Record<UnitOfMeasure, string> = { Piece: 'Pcs', Roll: 'Roll', Box: 'Box', Metre: 'Metre' }

/** The unit as a word, for sentences — "sold per piece", not "sold per pcs". */
export const unitWords: Record<UnitOfMeasure, string> = { Piece: 'piece', Roll: 'roll', Box: 'box', Metre: 'metre' }

export const vatLabels: Record<VatClass, string> = { Standard: 'Standard (16%)', ZeroRated: 'Zero-rated', Exempt: 'Exempt' }

export const fragilityLabels: Record<Fragility, string> = {
  NotFragile: '1 — Not fragile',
  Low: '2 — Low',
  Medium: '3 — Medium',
  High: '4 — High',
  ExtremelyFragile: '5 — Extremely fragile',
}

export const fragilityOrder: Fragility[] = ['NotFragile', 'Low', 'Medium', 'High', 'ExtremelyFragile']

/** Income accounts a category's sales may credit (§4). */
export const revenueAccounts = [
  { code: '4000', name: 'Sales – Lighting' },
  { code: '4010', name: 'Sales – Fittings' },
  { code: '4020', name: 'Sales – Solar' },
]

/** Margin on selling price, as the server computes it: (price − cost) ÷ price. */
export function marginPercent(price: number, cost: number): number | null {
  if (!(price > 0)) return null
  return Math.round(((price - cost) / price) * 10000) / 100
}
